// Threads rail.
import './ThreadsRail.css';
import type { JSX } from 'preact';
import { useState, useRef } from 'preact/hooks';
import {
  threads, threadId, groupId, paneOpen, drawerOpen, channelMeta,
  searchQuery, searchResults, searchLoading, searchError, highlightMessageId,
  searchOpen, isAdmin,
} from '../state';
import {
  openChat,
  startNewThread,
  deleteThread,
  loadThreads,
  searchThreads,
  clearSearch,
  openTaskPanel,
  threadCtxOf,
  navigateAttention,
} from '../actions';
import { requestChoice, requestConfirm } from './PromptModal';
import { tsKey } from '../utils';
import { Pane } from './Pane';
import { TrashIcon } from './ActionIcons';
import { RelativeTime } from './RelativeTime';
import type { Thread, SearchResult } from '../types';
import { AttentionBadge } from './AttentionBadge';
import { attentionError, threadAttentionCount } from '../attention-state';

function ThreadAttention({ thread }: { thread: Thread }) {
  const location = {
    agentGroupId: groupId.value, threadId: thread.threadId,
    channelType: thread.channelType || 'web', messagingGroupId: thread.messagingGroupId ?? null,
  };
  const count = threadAttentionCount(location);
  return count > 0 ? <>
    <span class="attention-separator" aria-hidden="true">{' \u00b7 '}</span>
    <button type="button" class="msg-action-btn thread-attention"
    disabled={!!attentionError.value}
    aria-label={attentionError.value
      ? `Input status unavailable. ${count} last known requests in ${thread.title}`
      : `${count} requests need your input. Open oldest request in ${thread.title}`}
    title={attentionError.value || 'Open oldest input request in this thread'}
    onClick={(event: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      void navigateAttention(location);
    }}>
    <AttentionBadge value={count} label={`${count} requests need your input`} />
    </button>
  </> : null;
}

function ThreadRow({ t }: { t: Thread }) {
  const ct = t.channelType || 'web';
  const meta = channelMeta(ct);
  const active = t.threadId === threadId.value;
  const pillTitle = `${meta.label}${t.counterparty ? ' · ' + t.counterparty : ''}`;
  const subTrailer = t.messageCount ? ' · ' + t.messageCount + ' msg' : '';
  const costStr = t.totalCost != null && t.totalCost > 0
    ? ' · ' + (t.totalCost >= 1 ? '$' + t.totalCost.toFixed(2) : '$' + t.totalCost.toFixed(3))
    : '';
  const lt = t.liveTasks;
  const head = lt && lt.length > 0 ? lt[0] : null;
  const liveTaskTitle = head
    ? [
        lt!.length > 1 ? `${lt!.length} scheduled tasks` : (head.paused ? 'Paused scheduled task' : 'Live scheduled task'),
        head.recurrence ? `recurs ${head.recurrence}` : null,
        head.nextRunAt ? `next ${new Date(head.nextRunAt).toLocaleString()}` : null,
        head.summary,
      ].filter(Boolean).join(' · ')
    : undefined;
  const onOpen = (ev: JSX.TargetedMouseEvent<HTMLDivElement>): void => {
    if ((ev.target as HTMLElement).classList.contains('del')) return;
    if (groupId.value) openChat(groupId.value, t.threadId, threadCtxOf(t)).catch(console.error);
    drawerOpen.threads.value = false;
  };
  const onDel = async (ev: JSX.TargetedMouseEvent<HTMLButtonElement>): Promise<void> => {
    ev.stopPropagation();
    const branches = t.forkChildren?.length ?? 0;
    if (branches > 0) {
      // A branch shares no mutable state with this thread, so deleting only
      // this one is safe and stays the default. Taking the branches too is a
      // separate, explicitly-labelled choice.
      const plural = branches === 1 ? 'branch' : 'branches';
      const wasWere = branches === 1 ? 'was' : 'were';
      const choice = await requestChoice({
        title: 'Delete thread',
        message:
          `"${t.title}"\n\n` +
          `${branches} ${plural} ${wasWere} started from this thread. They are independent copies — ` +
          'they keep working whether or not this thread survives.\n\n' +
          'Deleting a thread also cancels its outstanding approval requests. Already applied changes are not undone.',
        options: [
          { value: 'cancel', label: 'Cancel' },
          { value: 'one', label: 'Delete just this thread', tone: 'danger' },
          { value: 'all', label: `Delete this and ${branches} ${plural}`, tone: 'danger' },
        ],
      });
      if (choice !== 'one' && choice !== 'all') return;
      await deleteThread(t, choice === 'all');
      return;
    }
    const ok = await requestConfirm({
      title: 'Delete thread',
      message: `Delete this thread?\n\n"${t.title}"\n\n` +
        'This also cancels its outstanding approval requests. Already applied changes are not undone.',
      okLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await deleteThread(t);
  };
  const origin = t.forkedFrom;
  const branchCount = t.forkChildren?.length ?? 0;
  return (
    <div
      class={'thread' + (active ? ' active' : '') + (origin ? ' branch' : '')}
      data-id={t.threadId}
      onClick={onOpen}
    >
      <div class="title">
        {origin
          ? <span
              class={'fork-pill' + (origin.deleted ? ' orphan' : '')}
              title={origin.deleted
                ? 'Branched from a thread that has since been deleted'
                : `Branched from "${origin.title || 'another thread'}"`}
            >{'\u2442'}</span>
          : null}
        {ct !== 'web' ? <span class="ch-pill" title={pillTitle}>{meta.icon}</span> : null}
        <span class="title-text">{t.title}</span>
        {branchCount > 0
          ? <span
              class="fork-count"
              title={`${branchCount} ${branchCount === 1 ? 'branch was' : 'branches were'} started from this thread`}
            >{'\u2442'}{' '}{branchCount}</span>
          : null}
        {head ? (
          <button
            type="button"
            class={'task-badge' + (lt!.every((x) => x.paused) ? ' paused' : '')}
            title={liveTaskTitle}
            aria-label="Scheduled tasks"
            onClick={(ev: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
              ev.stopPropagation();
              if (groupId.value) openTaskPanel(groupId.value, t.threadId);
            }}
          >{'\u23F0'}</button>
        ) : null}
      </div>
      <div class="meta">
        <span><RelativeTime ts={t.lastActivityAt} />{subTrailer}{costStr}</span>
        <ThreadAttention thread={t} />
      </div>
      <div class="thread-actions">
        {isAdmin.value && (ct === 'web' || (t.sessionMode === 'per-thread' && !!t.messagingGroupId))
          ? <button type="button" class="msg-action-btn danger del" title="Delete thread" aria-label="Delete thread" onClick={onDel}><TrashIcon /></button>
          : null}
      </div>
    </div>
  );
}

function DmRow({ t }: { t: Thread }) {
  const ct = t.channelType || 'web';
  const meta = channelMeta(ct);
  const active = t.threadId === threadId.value;
  const onOpen = (): void => {
    if (groupId.value) openChat(groupId.value, t.threadId, threadCtxOf(t)).catch(console.error);
    drawerOpen.threads.value = false;
  };
  return (
    <div class={'thread dm' + (active ? ' active' : '')} data-id={t.threadId} onClick={onOpen}>
      <div class="title">
        <span class="ch-pill dm" title={meta.label}>{meta.icon}</span>
        <span class="title-text">{meta.label}</span>
      </div>
      <div class="meta">
        <span>
          <RelativeTime ts={t.lastActivityAt} />
          {t.counterparty ? ' \u00b7 ' + t.counterparty : ''}
          {t.messageCount ? ' \u00b7 ' + t.messageCount + ' msg' : ''}
        </span>
        <ThreadAttention thread={t} />
      </div>
    </div>
  );
}

function renderRow(t: Thread) {
  return t.kind === 'dm'
    ? <DmRow key={t.threadId} t={t} />
    : <ThreadRow key={t.threadId} t={t} />;
}

function ChannelSection({ ct, items, defaultOpen }: { ct: string; items: Thread[]; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = ct === 'web' || open;
  const meta = channelMeta(ct);
  const totalMsgs = items.reduce((sum, t) => sum + (t.messageCount || 0), 0);
  const lastActivityAt = items.reduce((latest, t) => {
    const ts = t.lastActivityAt || '';
    return ts > latest ? ts : latest;
  }, '');
  const handles = Array.from(new Set(items.map((t) => t.counterparty).filter((h): h is string => !!h)));
  const handleStr = handles.length === 0
    ? ''
    : handles.length === 1 ? handles[0]
      : `${handles[0]} +${handles.length - 1}`;
  return (
    <div class={'thread-section' + (expanded ? ' open' : ' collapsed')}>
      {ct === 'web'
        ? null
        : <button
          type="button"
          class="thread-section-header"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={expanded}
        >
          <span class="row">
            <span class="chev" aria-hidden="true">{expanded ? '\u25BE' : '\u25B8'}</span>
            <span class="ch-pill" aria-hidden="true">{meta.icon}</span>
            <span class="label">{meta.label}</span>
            <span class="count">
              {lastActivityAt ? <><RelativeTime ts={lastActivityAt} />{' \u00b7 '}</> : null}
              {items.length} threads {'\u00b7'} {totalMsgs} msg
            </span>
          </span>
          {handleStr
            ? <span class="handle" title={handles.join(', ')}>{handleStr}</span>
            : null}
        </button>}
      {expanded ? <div class="thread-section-body">{items.map(renderRow)}</div> : null}
    </div>
  );
}

function SearchResultRow({ r }: { r: SearchResult }) {
  const meta = channelMeta(r.channelType || 'web');
  const onOpen = (): void => {
    if (!groupId.value) return;
    const msgId = r.messageId;
    // For shared sessions (threadId is null), use the synthetic __dm:<mgId> ID
    // that the thread list uses for DM channels.
    const tid = r.threadId || (r.messagingGroupId ? `__dm:${r.messagingGroupId}` : null);
    if (!tid) return;
    drawerOpen.threads.value = false;
    const opts = threadCtxOf(threads.value.find((t) => t.threadId === tid)) ?? {
      channelType: r.channelType || 'web',
      messagingGroupId: r.messagingGroupId,
      canSend: false,
    };
    if (threadId.value === tid) {
      // Thread already open — scroll directly to the matched message.
      setTimeout(() => {
        const el = document.querySelector(`[data-msg-id="${CSS.escape(msgId)}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('highlight-pulse');
          setTimeout(() => el.classList.remove('highlight-pulse'), 2000);
        }
      }, 100);
    } else {
      // Different thread — set signal so MessageLog scrolls after history loads.
      highlightMessageId.value = msgId;
      openChat(groupId.value, tid, opts).catch(console.error);
    }
  };
  // Crop the snippet around the first match marker so the highlighted term
  // is always visible regardless of container width or line-clamp.
  const raw = r.snippet || '';
  const markerPos = raw.indexOf('>>>');
  const MAX_CHARS = 120;
  let cropped = raw;
  if (markerPos > MAX_CHARS / 2) {
    cropped = '…' + raw.slice(markerPos - Math.floor(MAX_CHARS / 3));
  }
  if (cropped.length > MAX_CHARS + 20) {
    cropped = cropped.slice(0, MAX_CHARS + 20) + '…';
  }
  const snippetHtml = cropped
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/&gt;&gt;&gt;/g, '<mark>').replace(/&lt;&lt;&lt;/g, '</mark>');
  return (
    <button type="button" class="thread search-result" onClick={onOpen}>
      <div class="title">
        {r.channelType && r.channelType !== 'web'
          ? <span class="ch-pill" title={meta.label}>{meta.icon}</span>
          : null}
        <span class={'dir-pill ' + r.direction}>
          <span aria-hidden="true">{r.direction === 'in' ? '\u2190' : '\u2192'}</span>
          <span class="search-result-direction">{r.direction === 'in' ? 'Incoming' : 'Outgoing'}</span>
        </span>
        <span class="snippet" dangerouslySetInnerHTML={{ __html: snippetHtml }} />
      </div>
      <div class="meta"><RelativeTime ts={r.timestamp} /></div>
    </button>
  );
}

function SearchResults() {
  const results = searchResults.value;
  const loading = searchLoading.value;
  const error = searchError.value;
  if (loading) {
    return <div class="search-results"><div class="empty" role="status" aria-live="polite">Searching…</div></div>;
  }
  if (error) {
    const onRetry = (): void => {
      if (groupId.value && searchQuery.value) {
        searchThreads(groupId.value, searchQuery.value).catch(console.error);
      }
    };
    return (
      <div class="search-results">
        <div class="empty search-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={onRetry}>Retry</button>
        </div>
      </div>
    );
  }
  if (!results) return null;
  if (results.length === 0) {
    return <div class="search-results"><div class="empty" role="status" aria-live="polite">No results</div></div>;
  }
  return (
    <div class="search-results" aria-label={`${results.length} search results`}>
      {results.map((r) => <SearchResultRow key={r.messageId} r={r} />)}
    </div>
  );
}

export function ThreadsRail() {
  const list = threads.value;
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const isSearching = searchOpen.value;

  const buckets = new Map<string, Thread[]>();
  for (const t of list) {
    const ct = t.channelType || 'web';
    if (!buckets.has(ct)) buckets.set(ct, []);
    buckets.get(ct)!.push(t);
  }
  const sections = Array.from(buckets.entries())
    .map(([ct, items]) => ({ ct, label: channelMeta(ct).label, items }))
    .sort((a, b) => {
      if (a.ct === 'web' && b.ct !== 'web') return -1;
      if (b.ct === 'web' && a.ct !== 'web') return 1;
      return a.label.localeCompare(b.label);
    });
  for (const s of sections) {
    s.items.sort((a, b) => tsKey(b.lastActivityAt) - tsKey(a.lastActivityAt));
  }

  const runSearch = (): void => {
    const q = searchQuery.peek().trim();
    if (!q) {
      clearSearch();
      return;
    }
    if (groupId.value) searchThreads(groupId.value, q).catch(console.error);
  };

  const onSearchKeyDown = (ev: JSX.TargetedKeyboardEvent<HTMLInputElement>): void => {
    if (ev.key === 'Enter') {
      runSearch();
    }
    if (ev.key === 'Escape') {
      ev.currentTarget.value = '';
      clearSearch();
    }
  };

  const onOpenSearch = (ev: JSX.TargetedMouseEvent<HTMLButtonElement>): void => {
    ev.stopPropagation();
    paneOpen.threads.value = true;
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const refreshThreads = (): void => {
    const gid = groupId.peek();
    const query = searchQuery.peek().trim();
    if (!gid) return;
    if (searchOpen.peek() && query) {
      searchThreads(gid, query).catch(console.error);
      return;
    }
    loadThreads(gid).catch(console.error);
  };

  const collapsedActions = (
    <>
      <button type="button" class="icon-btn rail-icon-btn collapsed-action-btn collapsed-primary-action-btn" title="New thread" aria-label="New thread" onClick={startNewThread}>+</button>
      <button type="button" class="icon-btn rail-icon-btn collapsed-action-btn" title="Search threads" aria-label="Search threads" onClick={onOpenSearch}>{'\uD83D\uDD0D'}</button>
    </>
  );

  return (
    <Pane
      paneKey="threads"
      name="threads-rail"
      label="Threads"
      collapsedActions={collapsedActions}
    >
      <div class="threads-actions rail-actions-row rail-divider-row">
        <button
          type="button"
          class="thread-action-btn accent-icon-btn rail-primary-action-btn new-thread-action-btn"
          title="New thread"
          aria-label="New thread"
          onClick={startNewThread}
        ><span class="plus-glyph" aria-hidden="true">+</span></button>
        <input
          ref={searchInputRef}
          type="text"
          class="rail-search-input search-input"
          value={searchQuery.value}
          placeholder="Search all threads..."
          onInput={(ev: JSX.TargetedInputEvent<HTMLInputElement>) => { searchQuery.value = ev.currentTarget.value; }}
          onKeyDown={onSearchKeyDown}
          aria-label="Search threads"
        />
        {isSearching ? (
          <button
            type="button"
            class="thread-action-btn rail-control-btn clear-search-btn"
            title="Close thread search"
            aria-label="Close thread search"
            onClick={clearSearch}
          >{'\u00D7'}</button>
        ) : null}
        <button
          type="button"
          class="thread-action-btn rail-control-btn refresh-btn"
          title={isSearching ? 'Refresh search' : 'Refresh threads'}
          aria-label={isSearching ? 'Refresh search' : 'Refresh threads'}
          onClick={refreshThreads}
        >{'\u21BB'}</button>
      </div>
      {isSearching
        ? <SearchResults />
        : (
          <div class="list" id="threads-list">
            {list.length === 0
              ? <div class="empty">No threads yet</div>
              : sections.map((s) => (
                <ChannelSection key={s.ct} ct={s.ct} items={s.items} defaultOpen={s.ct === 'web'} />
              ))}
          </div>
        )}
    </Pane>
  );
}
